import React, { useRef, useEffect, useState, useLayoutEffect } from 'react';
import ReactLoader from 'react-loading';

import { withTheme, ThemeProps } from 'styled-components';

import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { Airport } from 'models/airport';
import { City } from 'models/city';
import { AirportsState } from 'reducers/airports';

import ResultItem from './components/ResultItem';
import { Container, Header, Result, RecentSearches } from './styles';

interface Props extends AirportsState, ThemeProps<any> {
  show: boolean;
  placeHolder: string;
  minLengthForSearch: number;
  onClose: () => void;
  onSearchChange: (value: string) => void;
  onChange: (airport?: Airport) => void;
  type: string;
  zIndex?: number;
}

const AirportsSearch = ({
  show,
  placeHolder,
  bySearch,
  isLoading,
  minLengthForSearch,
  theme,
  onClose,
  onChange,
  onSearchChange,
  type,
  zIndex,
}: Props) => {
  const { t } = useStoreTranslation();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLocalLoading, setIsLocalLoading] = useState(false);

  useLayoutEffect(() => {
    searchInputRef.current?.focus();
    setSearchQuery('');
  }, [show]);

  useEffect(() => {
    setIsLocalLoading(isLoading);
  }, [isLoading]);

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (value && value.length > 2) {
      setIsLocalLoading(true);
    }

    setSearchQuery(value);
    onSearchChange(value);
  };

  const handleClickResultItem = (airport: Airport) => {
    onChange(airport);
    onClose();
  };

  const renderLoading = () => (
    <Result.Loading>
      <ReactLoader type="spin" color={theme.loading} width={60} height={60} />
    </Result.Loading>
  );

  const renderAirports = (airports: Airport[]) =>
    airports.map((airport: Airport, index: number) => (
      <ResultItem
        key={`airport-${type}-${airport.code}-${index}`}
        data={airport}
        onClick={handleClickResultItem}
        isRelatedAirport={false}
      />
    ));

  const renderCities = (cities: Airport[], airports: Airport[]) =>
    cities.map((city: City, index: number) => (
      <ResultItem
        key={`city-${city.code}-${index}`}
        data={city}
        onClick={handleClickResultItem}
        isCity
        isRelatedAirport={false}
      />
    ));

  const renderCitiesAndAirports = (cities: Airport[], airports: Airport[]) => (
    <>
      {cities.map((city: City, index: number) => {
        const cityAirports = airports.filter((element) => element.related_city === city.related_city);
        if (city.code.match('[A-Z]{3}') && cityAirports.length > 1) {
          const key = `city-airport-cont-more-one-${city.code}-${index}`;
          return (
            <div id={key} key={key}>
              <ResultItem
                key={`city-airport-${type}-${city.code}-${index}`}
                data={city}
                onClick={handleClickResultItem}
                isCity={true}
                isRelatedAirport={false}
              />

              {airports
                .filter((e) => e.related_city === city.related_city && e.country === city.country)
                .map((airport: Airport, index: number) => (
                  <ResultItem
                    key={`airport-city-${type}-${airport.code}-${index}`}
                    data={airport}
                    isCity={false}
                    onClick={handleClickResultItem}
                    isRelatedAirport={true}
                  />
                ))}
            </div>
          );
        }

        if (city.code.match('[A-Z]{3}') && cityAirports.length === 1) {
          const key = `city-airport-only-one-cont-${city.code}-${index}`;
          return (
            <div id={key} key={key}>
              <ResultItem
                key={`city-${type}-${city.code}-${index}`}
                data={cityAirports.first()}
                onClick={handleClickResultItem}
                isCity={false}
                isRelatedAirport={false}
              />
            </div>
          );
        }
      })}

      {airports.map((airport: City, index: number) => {
        const airportCity = cities.filter((element) => element.related_city === airport.related_city);

        if (airport.code.match('[A-Z]{3}') && airportCity.length === 0) {
          return (
            <ResultItem
              key={`airport2-${type}-${airport.code}-${index}`}
              data={airport}
              isCity={false}
              onClick={handleClickResultItem}
              isRelatedAirport={false}
            />
          );
        } else {
          return '';
        }
      })}
    </>
  );

  const renderResults = () => {
    if (!bySearch) return null;

    if (type === 'DEPARTURE') {
      if (!bySearch.departure) return null;
      if (
        !isLocalLoading &&
        bySearch.departure.cities.length === 0 &&
        bySearch.departure.airports.length === 0 &&
        searchQuery.length > minLengthForSearch
      ) {
        return (
          <Result.NotFound>
            <h2>Ops!</h2>
            <span>{t('error.notFound')}</span>
          </Result.NotFound>
        );
      }

      return (
        <Result data-testid="airport-search-result-list">
          {searchQuery.length <= minLengthForSearch && (
            <RecentSearches>{t('airportsSearch.recentSearches')}</RecentSearches>
          )}
          {isLocalLoading && renderLoading()}
          {bySearch.departure.cities.length !== 0 &&
            bySearch.departure.cities.length !== 0 &&
            renderCitiesAndAirports(bySearch.departure.cities, bySearch.departure.airports)}
          {bySearch.departure.cities.length === 0 &&
            bySearch.departure.airports.length !== 0 &&
            renderAirports(bySearch.departure.airports)}
          {bySearch.departure.cities.length !== 0 &&
            bySearch.departure.airports.length === 0 &&
            renderCities(bySearch.departure.cities, bySearch.departure.airports)}
        </Result>
      );
    }

    if (type === 'ARRIVAL') {
      if (!bySearch.arrival) return null;
      if (
        !isLocalLoading &&
        bySearch.arrival.cities.length === 0 &&
        bySearch.arrival.airports.length === 0 &&
        searchQuery.length > minLengthForSearch
      ) {
        return (
          <Result.NotFound>
            <h2>Ops!</h2>
            <span>{t('error.notFound')}</span>
          </Result.NotFound>
        );
      }

      return (
        <Result data-testid="airport-search-result-list">
          {searchQuery.length <= minLengthForSearch && (
            <RecentSearches>{t('airportsSearch.recentSearches')}</RecentSearches>
          )}
          {isLocalLoading && renderLoading()}
          {bySearch.arrival.cities.length !== 0 &&
            bySearch.arrival.cities.length !== 0 &&
            renderCitiesAndAirports(bySearch.arrival.cities, bySearch.arrival.airports)}
          {bySearch.arrival.cities.length === 0 &&
            bySearch.arrival.airports.length !== 0 &&
            renderAirports(bySearch.arrival.airports)}
          {bySearch.arrival.cities.length !== 0 &&
            bySearch.arrival.airports.length === 0 &&
            renderCities(bySearch.arrival.cities, bySearch.arrival.airports)}
        </Result>
      );
    }

    if (type === 'DESTINATION') {
      if (!bySearch.destination) return null;
      if (
        !isLocalLoading &&
        bySearch.destination.cities.length === 0 &&
        bySearch.destination.airports.length === 0 &&
        searchQuery.length > minLengthForSearch
      ) {
        return (
          <Result.NotFound>
            <h2>Ops!</h2>
            <span>{t('error.notFound')}</span>
          </Result.NotFound>
        );
      }

      return (
        <Result data-testid="airport-search-result-list">
          {searchQuery.length <= minLengthForSearch && (
            <RecentSearches>{t('airportsSearch.recentSearches')}</RecentSearches>
          )}
          {isLocalLoading && renderLoading()}
          {bySearch.destination.cities.length !== 0 &&
            bySearch.destination.cities.length !== 0 &&
            renderCitiesAndAirports(bySearch.destination.cities, bySearch.destination.airports)}
          {bySearch.destination.cities.length === 0 &&
            bySearch.destination.airports.length !== 0 &&
            renderAirports(bySearch.destination.airports)}
          {bySearch.destination.cities.length !== 0 &&
            bySearch.destination.airports.length === 0 &&
            renderCities(bySearch.destination.cities, bySearch.destination.airports)}
        </Result>
      );
    }

    if (type === 'MULTIDESTINATION_DEPARTURE') {
      if (!bySearch.departure_multidestination) return null;
      if (
        !isLocalLoading &&
        bySearch.departure_multidestination.cities.length === 0 &&
        bySearch.departure_multidestination.airports.length === 0 &&
        searchQuery.length > minLengthForSearch
      ) {
        return (
          <Result.NotFound>
            <h2>Ops!</h2>
            <span>{t('error.notFound')}</span>
          </Result.NotFound>
        );
      }

      return (
        <Result data-testid="airport-search-result-list">
          {searchQuery.length <= minLengthForSearch && bySearch.arrival_multidestination !== undefined && (
            <>
              <RecentSearches>{t('airportsSearch.recentSearches')}</RecentSearches>

              {bySearch.arrival_multidestination?.cities.length > 0 &&
                bySearch.arrival_multidestination?.airports.length > 0 &&
                renderCitiesAndAirports(
                  bySearch.arrival_multidestination?.cities,
                  bySearch.arrival_multidestination?.airports,
                )}
              {bySearch.arrival_multidestination?.cities.length === 0 &&
                bySearch.arrival_multidestination?.airports.length !== 0 &&
                renderAirports(bySearch.arrival_multidestination?.airports)}
              {bySearch.arrival_multidestination?.cities.length !== 0 &&
                bySearch.arrival_multidestination?.airports.length === 0 &&
                renderCities(bySearch.arrival_multidestination?.cities, bySearch.arrival_multidestination?.airports)}
            </>
          )}
          {isLocalLoading && renderLoading()}

          {searchQuery.length > minLengthForSearch && (
            <>
              {bySearch.departure_multidestination.cities.length !== 0 &&
                bySearch.departure_multidestination.airports.length !== 0 &&
                renderCitiesAndAirports(
                  bySearch.departure_multidestination.cities,
                  bySearch.departure_multidestination.airports,
                )}
              {bySearch.departure_multidestination.cities.length === 0 &&
                bySearch.departure_multidestination.airports.length !== 0 &&
                renderAirports(bySearch.departure_multidestination.airports)}
              {bySearch.departure_multidestination.cities.length !== 0 &&
                bySearch.departure_multidestination.airports.length === 0 &&
                renderCities(bySearch.departure_multidestination.cities, bySearch.departure_multidestination.airports)}
            </>
          )}
        </Result>
      );
    }

    if (type === 'MULTIDESTINATION_ARRIVAL') {
      if (!bySearch.arrival_multidestination) return null;
      if (
        !isLocalLoading &&
        bySearch.arrival_multidestination.cities.length === 0 &&
        bySearch.arrival_multidestination.airports.length === 0 &&
        searchQuery.length > minLengthForSearch
      ) {
        return (
          <Result.NotFound>
            <h2>Ops!</h2>
            <span>{t('error.notFound')}</span>
          </Result.NotFound>
        );
      }

      return (
        <Result data-testid="airport-search-result-list">
          {searchQuery.length <= minLengthForSearch && bySearch.departure_multidestination !== undefined && (
            <>
              <RecentSearches>{t('airportsSearch.recentSearches')}</RecentSearches>

              {bySearch.departure_multidestination?.cities.length !== 0 &&
                bySearch.departure_multidestination?.airports.length !== 0 &&
                renderCitiesAndAirports(
                  bySearch.departure_multidestination?.cities,
                  bySearch.departure_multidestination?.airports,
                )}
              {bySearch.departure_multidestination?.cities.length === 0 &&
                bySearch.departure_multidestination?.airports.length !== 0 &&
                renderAirports(bySearch.departure_multidestination?.airports)}
              {bySearch.departure_multidestination?.cities.length !== 0 &&
                bySearch.departure_multidestination?.airports.length === 0 &&
                renderCities(
                  bySearch.departure_multidestination?.cities,
                  bySearch.departure_multidestination?.airports,
                )}
            </>
          )}
          {isLocalLoading && renderLoading()}

          {searchQuery.length > minLengthForSearch && (
            <>
              {bySearch.arrival_multidestination.cities.length !== 0 &&
                bySearch.arrival_multidestination.airports.length !== 0 &&
                renderCitiesAndAirports(
                  bySearch.arrival_multidestination.cities,
                  bySearch.arrival_multidestination.airports,
                )}
              {bySearch.arrival_multidestination.cities.length === 0 &&
                bySearch.arrival_multidestination.airports.length !== 0 &&
                renderAirports(bySearch.arrival_multidestination.airports)}
              {bySearch.arrival_multidestination.cities.length !== 0 &&
                bySearch.arrival_multidestination.airports.length === 0 &&
                renderCities(bySearch.arrival_multidestination.cities, bySearch.arrival_multidestination.airports)}
            </>
          )}
        </Result>
      );
    }
  };

  return (
    <ApplicationOverlayContent sidebar show={show} onClose={onClose} zIndex={zIndex}>
      <Container>
        <Header>
          <Header.Search
            ref={searchInputRef}
            value={searchQuery}
            placeholder={placeHolder}
            onChange={handleSearchInputChange}
            data-testid="airport-search-input"
          />
          <Header.CloseIcon onClick={onClose} />
        </Header>
        {renderResults()}
      </Container>
    </ApplicationOverlayContent>
  );
};

export default withTheme(AirportsSearch);
